import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowDown01Icon,
  ArrowUp01Icon,
  Chatting01Icon,
  Mail02Icon,
  Message02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import type { IconSvgElement } from "@hugeicons/react";
import { useAuthContext } from "providers/Auth";
import { Tabs } from "shared/components/BaseTabs";
import { Loader } from "shared/components/Loader";
import { cx } from "shared/utils/classnames";
import {
  MESSAGE_POPUP_MENU_Z_CLASS,
  MESSAGE_POPUP_MODAL_Z_INDEX,
  MESSAGE_POPUP_Z_CLASS
} from "softphone/stacking";
import { ConversationPane } from "views/inbox/components/ConversationPane";
import {
  shouldShowChannelTabs,
  useComposerTabs
} from "views/inbox/hooks/useComposerTabs";
import { useConversationSession } from "views/inbox/hooks/useConversationSession";
import { useEmbeddedInboxContext } from "views/inbox/hooks/useEmbeddedInboxContext";
import { InboxProvider } from "views/inbox/context/InboxContext";
import { InboxEmbedProvider } from "views/inbox/context/InboxEmbedContext";
import type { ComposerTab } from "views/inbox/components/constants";
import { crmKeys, type CrmContact } from "views/inbox/api/crm";
import type { Application } from "types/application";
import { MessagePopupHeader } from "./components/MessagePopupHeader";
import { applicantName } from "./applicantName";
import { useMessagePopupConversation } from "./hooks/useMessagePopupConversation";
import { useMessagePopupDeepLink } from "./hooks/useMessagePopupDeepLink";
import { useMessagePopupStore, type MessagePopupTarget } from "./store";
import {
  MESSAGE_POPUP_CARD_CLASS,
  MESSAGE_POPUP_DRAWER_CLASS,
  MESSAGE_POPUP_EMPTY_DESCRIPTION,
  MESSAGE_POPUP_EMPTY_DESCRIPTION_GENERIC,
  MESSAGE_POPUP_SMS_NOTE
} from "./constants";

// Design shows Chat, SMS and Email only — no Call and no Manual. Order matters:
// this is the order the chips are drawn in (2152:* wave reordered SMS first).
const POPUP_CHANNELS: ComposerTab[] = ["chat", "sms", "email"];

// The pop-up chips use the design's own icon set at 16px (chatting-01 /
// message-02 / mail-02) — /inbox keeps composerTabConfig's mapping, so the
// swap is scoped here.
const POPUP_CHIP_ICONS: Partial<Record<ComposerTab, IconSvgElement>> = {
  chat: Chatting01Icon,
  sms: Message02Icon,
  email: Mail02Icon
};

// The From/To routing block under the chips (design 2152:16771/16996). The
// floating card collapses it behind "Show participants" (closed by default per
// the design sticky); the drawer always shows it. The composer PORTALS the
// actual selects into the inner div, so they stay mounted — and keep reporting
// their selection to the send path — even while collapsed.
const ParticipantsSection = ({
  layout,
  open,
  onToggle,
  onElement
}: {
  layout: "floating" | "drawer";
  open: boolean;
  onToggle: () => void;
  onElement: (el: HTMLElement | null) => void;
}) => {
  const expanded = layout === "drawer" || open;
  return (
    <div
      className={cx(
        "shrink-0 border-b border-alpha-100 bg-white px-5 pt-1",
        expanded ? "pb-4" : "pb-2"
      )}
    >
      {layout === "floating" ? (
        <button
          type="button"
          onClick={onToggle}
          className="flex items-center gap-2 text-xs font-bold text-content-200"
        >
          <HugeiconsIcon
            icon={open ? ArrowUp01Icon : ArrowDown01Icon}
            size={20}
          />
          {open ? "Hide details" : "Show details"}
        </button>
      ) : null}
      <div
        ref={onElement}
        className={cx(
          layout === "floating" ? "pt-2" : "",
          expanded ? "" : "hidden"
        )}
      />
    </div>
  );
};

const targetKey = (target: MessagePopupTarget) =>
  [target.contactId, target.applicantId, target.applicationId, target.leadId]
    .map((value) => value ?? "")
    .join(":");

/**
 * The floating message pop-up (CRM-1133): a second entry point into the same
 * conversation the Inbox shows, opened from a lead, an application, an applicant
 * card or a list row. Mounted once inside the router (providers/Routes.tsx) so
 * navigating never unmounts an open conversation or an unsent draft.
 */
export const MessagePopup = () => {
  const target = useMessagePopupStore((state) => state.target);
  // A notification email can link straight into a conversation.
  useMessagePopupDeepLink();

  if (!target) return null;

  // Remount on a new target so no applicant/channel/draft state leaks between
  // two different conversations.
  return <MessagePopupContent key={targetKey(target)} target={target} />;
};

const MessagePopupContent = ({ target }: { target: MessagePopupTarget }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const close = useMessagePopupStore((state) => state.close);
  const layout = useMessagePopupStore((state) => state.layout);
  const setLayout = useMessagePopupStore((state) => state.setLayout);
  const { type } = useAuthContext();

  const [channel, setChannel] = useState<ComposerTab>(target.channel ?? "chat");
  // The From/To routing block under the chips ("participants"). Collapsed by
  // default on the floating card (design sticky); the drawer always shows it.
  // The composer portals the actual selects into `participantsEl`, so they stay
  // mounted (and keep reporting their selection) even while collapsed.
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [participantsEl, setParticipantsEl] = useState<HTMLElement | null>(
    null
  );

  const {
    contactId,
    hasContactError,
    conversationId,
    isConversationLoading,
    unreadCount,
    applicants,
    selectedApplicant,
    selectedApplicantId,
    setSelectedApplicantId,
    onConversationResolved,
    applicationId
  } = useMessagePopupConversation(target);

  const { accessToken, isTokenLoading } = useConversationSession({
    conversationId,
    contactId
  });

  const { visibleTabs, inviteModal } = useComposerTabs(
    contactId ? String(contactId) : undefined,
    {
      // The Invite-to-apply modal is opened from the disabled Chat chip's
      // tooltip, so it has to clear the pop-up it was opened from.
      inviteModalZIndex: MESSAGE_POPUP_MODAL_Z_INDEX,
      // Opened from a lead page: pre-select that lead's community/unit and link
      // the application to the lead, like the page's own Invite button does.
      inviteLeadId: target.leadId
    }
  );

  // The pop-up's chips are the composer's entitlement-filtered tabs, narrowed to
  // the three the design shows and reordered to match it. Reusing the list keeps
  // the disabled-chat chip and its "Invite to apply" tooltip identical to the
  // Inbox's.
  const chips = useMemo(
    () =>
      POPUP_CHANNELS.map((value) =>
        visibleTabs.find((tab) => tab.value === value)
      )
        .filter((tab): tab is (typeof visibleTabs)[number] => Boolean(tab))
        .map((tab) => {
          const icon = POPUP_CHIP_ICONS[tab.value];
          return icon
            ? {
                ...tab,
                leftElement: {
                  type: "icon" as const,
                  component: icon,
                  hugeIconProps: { size: 16 }
                }
              }
            : tab;
        }),
    [visibleTabs]
  );

  // Fall back when the chosen chip is missing or disabled for this contact (e.g.
  // Chat on a lead who never applied).
  const activeChannel = useMemo(() => {
    const requested = chips.find((chip) => chip.value === channel);
    if (requested && !requested.disabled) return channel;
    return chips.find((chip) => !chip.disabled)?.value ?? channel;
  }, [chips, channel]);

  // A partner without CRM has nothing to switch to, so the lone "Chat" chip is
  // dropped and the thread gets the row's height back (CRM-1325).
  const showChannelChips = shouldShowChannelTabs({
    isBoomCRM: type.isBoomCRM,
    tabs: chips
  });

  const subject = useMemo(
    () =>
      applicationId
        ? ({ type: "application", id: String(applicationId) } as const)
        : undefined,
    [applicationId]
  );

  const inboxContext = useEmbeddedInboxContext({
    conversationId,
    draftContactId: conversationId ? undefined : contactId,
    unreadCount,
    subject,
    onConversationResolved
  });

  const supportAgent = useMemo(
    () =>
      selectedApplicant
        ? {
            applicantId: selectedApplicant.id,
            active: selectedApplicant.application_support_agent_active ?? true
          }
        : undefined,
    [selectedApplicant]
  );

  // The header orb and the toggle's `active` are DERIVED from cached queries
  // (the application here, the CRM contact on the fallback path), so the flip
  // is mirrored into both caches the moment the switch moves — the orb used to
  // lag a PATCH + refetch behind it. The toggle calls back again with the old
  // value if the PATCH fails, which reverts the same way.
  const handleSupportAgentChanged = useCallback(
    (active: boolean, applicantId: number) => {
      const patchApplicant = <T extends { id: number }>(applicant: T): T =>
        applicant.id === applicantId
          ? { ...applicant, application_support_agent_active: active }
          : applicant;
      if (applicationId)
        queryClient.setQueryData<Application>(
          ["APPLICATION", String(applicationId)],
          (current) =>
            current
              ? {
                  ...current,
                  applicants: current.applicants.map(patchApplicant)
                }
              : current
        );
      if (contactId)
        queryClient.setQueryData<CrmContact>(
          crmKeys.contact(String(contactId)),
          (current) =>
            current
              ? {
                  ...current,
                  applicants: current.applicants?.map(patchApplicant)
                }
              : current
        );
    },
    [queryClient, applicationId, contactId]
  );

  const shellClass = cx(
    layout === "drawer" ? MESSAGE_POPUP_DRAWER_CLASS : MESSAGE_POPUP_CARD_CLASS,
    MESSAGE_POPUP_Z_CLASS
  );

  const contactName = target.contactName || "Conversation";
  // On an application the header names the applicant the dropdown selected, so
  // the two can never disagree — the CRM contact behind an applicant may carry
  // a different name (renamed lead, merged contact). Otherwise the
  // conversation's resolved contact name, else what the trigger passed.
  const selectedApplicantName = selectedApplicant
    ? applicantName(selectedApplicant) || selectedApplicant.email
    : undefined;
  const headerName = (resolvedName?: string) =>
    selectedApplicantName ??
    (resolvedName && resolvedName !== "Unknown" ? resolvedName : contactName);

  const header = (
    name: string,
    paneSupportAgent?: { applicantId: number; active: boolean }
  ) => (
    <MessagePopupHeader
      contactName={name}
      layout={layout}
      applicants={applicants}
      selectedApplicantId={selectedApplicantId}
      supportAgent={supportAgent ?? paneSupportAgent}
      onSupportAgentChanged={handleSupportAgentChanged}
      onSelectApplicant={setSelectedApplicantId}
      onLayoutChange={setLayout}
      onGoToInbox={() => {
        close();
        navigate(contactId ? `/inbox?contact=${contactId}` : "/inbox");
      }}
      onClose={close}
    />
  );

  // A conversation we cannot address at all (the contact could not be resolved
  // or linked) is a dead end — say so instead of spinning forever.
  if (hasContactError)
    return (
      <div className={shellClass} role="dialog" aria-label="Conversation">
        {header(headerName())}
        <div className="flex flex-1 items-center justify-center px-6">
          <p className="text-center text-sm text-content-300">
            This contact can&apos;t be messaged yet. Add an email or phone
            number to the applicant and try again.
          </p>
        </div>
      </div>
    );

  if (isTokenLoading || !accessToken || !contactId || isConversationLoading)
    return (
      <div className={shellClass} role="dialog" aria-label="Conversation">
        {header(headerName())}
        <div className="flex flex-1 items-center justify-center">
          <Loader size={24} />
        </div>
      </div>
    );

  return (
    <div className={shellClass} role="dialog" aria-label="Conversation">
      <InboxProvider value={inboxContext}>
        <InboxEmbedProvider
          menuZClass={MESSAGE_POPUP_MENU_Z_CLASS}
          modalZIndex={MESSAGE_POPUP_MODAL_Z_INDEX}
          compact
          layout={layout}
          participantsEl={participantsEl}
        >
          <ConversationPane
            conversationId={conversationId}
            activeChannel={activeChannel}
            onChannelChange={setChannel}
            filterByChannel
            hideComposerTabs
            emptyStateVariant={layout === "drawer" ? "drawer" : "card"}
            emptyStateDescription={
              // The chat copy promises SMS delivery, which is only true of chat.
              activeChannel === "chat"
                ? MESSAGE_POPUP_EMPTY_DESCRIPTION
                : MESSAGE_POPUP_EMPTY_DESCRIPTION_GENERIC
            }
            className="flex min-h-0 flex-1 flex-col bg-background-900"
            scrollClassName={cx(
              "flex-1 overflow-y-auto scroll-on-hover",
              // 24px between the last bubble and the composer in the drawer.
              layout === "drawer" ? "px-6 pt-4 pb-6" : "px-5 py-3"
            )}
            footer={
              activeChannel === "chat" ? (
                <p
                  className={cx(
                    "shrink-0 bg-white pb-3 text-xs font-medium text-content-300",
                    layout === "drawer" ? "px-6" : "px-5"
                  )}
                >
                  {MESSAGE_POPUP_SMS_NOTE}
                </p>
              ) : null
            }
            renderHeader={({
              contactName: resolvedName,
              supportAgent: paneSupportAgent
            }) => (
              <>
                {header(headerName(resolvedName), paneSupportAgent)}
                {showChannelChips ? (
                  <div className="shrink-0 border-b border-alpha-100 bg-white px-5 py-2">
                    <Tabs
                      tabs={chips}
                      active={activeChannel}
                      onChange={setChannel}
                      tabStyle="ghost-pink"
                      size="sm"
                      fullWidthTabs={false}
                      className={cx(
                        "[&_button]:rounded-full [&_button]:font-medium",
                        "[&_button[aria-selected=false]]:text-content-300",
                        // The design draws 28px chips on the floating card and the
                        // default 36px ones in the taller drawer.
                        layout === "floating" ? "[&_button]:h-7" : ""
                      )}
                      disabledTooltipZClass={MESSAGE_POPUP_MENU_Z_CLASS}
                    />
                  </div>
                ) : null}
                {activeChannel === "email" || activeChannel === "sms" ? (
                  <ParticipantsSection
                    layout={layout}
                    open={participantsOpen}
                    onToggle={() => setParticipantsOpen((open) => !open)}
                    onElement={setParticipantsEl}
                  />
                ) : null}
              </>
            )}
          />
        </InboxEmbedProvider>
      </InboxProvider>
      {inviteModal}
    </div>
  );
};
